import { useSelector } from 'react-redux'
import moment from 'moment'
import { Feature, useFeatureFlag } from '@src/components/FeatureFlag'
import { useGetAllOwnSensorSubscriptions } from '@src/hooks/useHasOwnSensorSubscription'
import { insurancePolicySelector } from '@src/screens/NutritionistHub/models/nutritionistHub.selectors'
import { userPropertiesSelector, userSelector } from '@src/selectors/app'
import { Storage } from '@src/utils'
import { Bugsnag } from '@src/config'
import { useVideoCallProductType } from '@src/hooks/useVideoCallProductType.ts'
import { ByosVideoCallsUpsellStep } from '../constants'
import {
  getValidatedByosVideoCallsUpsellStep,
  getMinimumMinutesToWaitBetweenSteps,
  getMinimumMinutesToWaitToStartCampaign,
} from '../utils'

export const useGetByosVideoCallUpsellNextStep = (): ByosVideoCallsUpsellStep | undefined => {
  const activeOwnSensorSubscriptions = useGetAllOwnSensorSubscriptions()
  const { isInsuranceVideoCallProductType } = useVideoCallProductType()

  // This hook supports two campaign models:
  // 1) Inbox enabled: backend is source of truth via user property.
  // 2) Inbox disabled: legacy local-storage progression.
  const notificationInboxEnabled = useFeatureFlag(Feature.NotificationInbox)

  const insurancePolicy = useSelector(insurancePolicySelector)
  const user = useSelector(userSelector)
  const userProperties = useSelector(userPropertiesSelector)

  // In inbox mode backend orchestration writes `userProperties.byosVideoCallsUpsell`.
  // We render whichever step backend currently says should be shown next.
  if (notificationInboxEnabled) {
    return getValidatedByosVideoCallsUpsellStep(userProperties?.byosVideoCallsUpsell?.value)
  }

  if (!isInsuranceVideoCallProductType) {
    return
  }

  if (insurancePolicy) {
    return
  }

  if (activeOwnSensorSubscriptions.length === 0) {
    return
  }

  const latestStepFromStorage = Storage.get(
    `${Storage.BYOS_VIDEO_CALLS_UPSELL_LAST_STEP_KEY}_${user?.id}`,
  )

  if (!latestStepFromStorage) {
    const latestActiveOwnSensorSubscription = activeOwnSensorSubscriptions.sort(
      (a, b) => new Date(b.subscribedAt).getTime() - new Date(a.subscribedAt).getTime(),
    )[0]

    const minutesSinceSubscribing = moment().diff(
      latestActiveOwnSensorSubscription.subscribedAt,
      'minutes',
    )

    if (minutesSinceSubscribing < getMinimumMinutesToWaitToStartCampaign()) {
      return
    }
    return ByosVideoCallsUpsellStep.ActionItem
  }

  let latestStep: ByosVideoCallsUpsellStep
  if (
    Object.values(ByosVideoCallsUpsellStep).includes(
      latestStepFromStorage as ByosVideoCallsUpsellStep,
    )
  ) {
    latestStep = latestStepFromStorage as ByosVideoCallsUpsellStep
  } else {
    Bugsnag.notify(
      new Error(`BYOS video call upsell - unknown step found ${latestStepFromStorage}`),
    )
    return
  }

  // Legacy local flow is now fixed to the final production sequence.
  const lastStepInFlow = ByosVideoCallsUpsellStep.FreeConsultationActionItem

  if (latestStep === lastStepInFlow) {
    return
  }

  const lastInteractionTimestamp = Storage.get(
    `${Storage.BYOS_VIDEO_CALLS_UPSELL_LAST_INTERACTION_TIMESTAMP_KEY}_${user?.id}`,
  )

  if (!lastInteractionTimestamp) {
    Bugsnag.notify(
      new Error(
        `BYOS video call upsell - Last interaction timestamp not found for step ${latestStep}`,
      ),
    )
    return
  }

  const minutesSinceLastInteraction = moment().diff(lastInteractionTimestamp, 'minutes')
  const minimumMinutesToWaitBetweenSteps = getMinimumMinutesToWaitBetweenSteps()

  if (minimumMinutesToWaitBetweenSteps > minutesSinceLastInteraction) {
    return
  }

  if (latestStep === ByosVideoCallsUpsellStep.ActionItem) {
    return ByosVideoCallsUpsellStep.InitialModal
  } else if (latestStep === ByosVideoCallsUpsellStep.InitialModal) {
    return ByosVideoCallsUpsellStep.ChatlikeNotification
  } else if (latestStep === ByosVideoCallsUpsellStep.ChatlikeNotification) {
    return ByosVideoCallsUpsellStep.FreeConsultationModal
  } else if (latestStep === ByosVideoCallsUpsellStep.FreeConsultationModal) {
    return ByosVideoCallsUpsellStep.FreeConsultationChatlikeNotification
  } else {
    return ByosVideoCallsUpsellStep.FreeConsultationActionItem
  }
}
